import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook, Mail, MapPin, Heart, Sparkles } from "lucide-react";
import PromoBanner from "@/components/PromoBanner";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/menu", label: "Menu" },
  { to: "/custom-orders", label: "Custom Orders" },
  { to: "/gallery", label: "Gallery" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

function Logo() {
  return (
    <Link
      to="/"
      data-testid="nav-logo"
      className="flex items-center gap-3 group"
    >
      <img
        src="/logo.png?v=2"
        alt="Sakura Treats Co."
        className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover shadow-sakura ring-1 ring-sakura-gold/40 transition group-hover:scale-[1.03]"
      />
        <span className="hidden sm:flex flex-col leading-tight">
          <span className="font-serif text-xl text-sakura-ink">Sakura Treats</span>
          <span className="text-[9px] tracking-[0.18em] uppercase text-sakura-500 font-semibold">Serving Spring Hill, FL · Pasco County · Tampa Bay Area</span>
        </span>
    </Link>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <div className="min-h-screen flex flex-col bg-background text-sakura-ink">
      {!isAdminRoute && <PromoBanner />}
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-sakura-200/60" data-testid="site-header">
        <div className="mx-auto max-w-7xl px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-semibold transition ${
                    isActive ? "bg-sakura-200 text-sakura-500" : "text-sakura-ink/80 hover:text-sakura-500 hover:bg-sakura-100"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            <Link
              to="/custom-orders"
              data-testid="header-order-cta"
              className="btn-primary text-sm"
            >
              <Sparkles className="h-4 w-4" /> Start an Order
            </Link>
          </div>
          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            className="lg:hidden h-10 w-10 grid place-items-center rounded-full bg-sakura-100 text-sakura-500"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-sakura-200/70 bg-white/90 backdrop-blur-xl" data-testid="mobile-menu">
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-2xl text-sm font-semibold ${
                      isActive ? "bg-sakura-200 text-sakura-500" : "text-sakura-ink"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/custom-orders"
                onClick={() => setOpen(false)}
                className="btn-primary mt-3 w-full"
                data-testid="mobile-order-cta"
              >
                <Sparkles className="h-4 w-4" /> Start an Order
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      {!isAdminRoute && (
        <footer className="bg-sakura-100/60 border-t border-sakura-200 mt-10" data-testid="site-footer">
          <div className="mx-auto max-w-7xl px-6 md:px-12 py-14 grid gap-10 md:grid-cols-4">
            <div>
              <Logo />
              <p className="mt-4 text-sm text-sakura-soft max-w-xs">
                Small-batch, handcrafted custom treats — made fresh to order in the Tampa Bay / Spring Hill area.
              </p>
            </div>
            <div>
              <h4 className="text-base font-serif text-sakura-ink mb-3">Explore</h4>
              <ul className="space-y-1.5 text-sm">
                {NAV.slice(0, 4).map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="text-sakura-soft hover:text-sakura-500">{n.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-base font-serif text-sakura-ink mb-3">More</h4>
              <ul className="space-y-1.5 text-sm">
                {NAV.slice(4).map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="text-sakura-soft hover:text-sakura-500">{n.label}</Link>
                  </li>
                ))}
                <li><Link to="/pay" className="text-sakura-soft hover:text-sakura-500" data-testid="footer-pay-link">Pay Deposit</Link></li>
                <li><Link to="/admin/login" className="text-sakura-soft hover:text-sakura-500" data-testid="footer-admin-link">Admin</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-serif text-sakura-ink mb-3">Say Hi</h4>
              <ul className="space-y-2 text-sm text-sakura-soft">
                <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-sakura-400" /> Tampa Bay / Spring Hill, FL</li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-sakura-400" />
                  <a href="mailto:sakuratreats.shop@gmail.com" className="hover:text-sakura-500" data-testid="footer-email">sakuratreats.shop@gmail.com</a>
                </li>
              </ul>
              <div className="mt-4 flex items-center gap-2">
                <a href="https://www.instagram.com/sakuratreats_?igsh=MXFnNnM0NTl3YnBnMQ%3D%3D&utm_source=qr" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-social-instagram"
                   className="h-10 w-10 grid place-items-center rounded-full bg-white border border-sakura-200 text-sakura-500 hover:bg-sakura-200 transition">
                  <Instagram className="h-4 w-4" />
                </a>
                <a href="https://www.facebook.com/share/1J9pmaK1kw/?mibextid=wwXIfr" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="footer-social-facebook"
                   className="h-10 w-10 grid place-items-center rounded-full bg-white border border-sakura-200 text-sakura-500 hover:bg-sakura-200 transition">
                  <Facebook className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-sakura-200/60">
            <div className="mx-auto max-w-7xl px-6 md:px-12 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-sakura-soft">
              <span className="flex items-center gap-1.5"><Heart className="h-3.5 w-3.5 text-sakura-400" /> © {new Date().getFullYear()} Sakura Treats — Made with love in Florida.</span>
              <div className="flex items-center gap-4">
                <Link to="/privacy" className="hover:text-sakura-500" data-testid="footer-privacy">Privacy</Link>
                <Link to="/terms" className="hover:text-sakura-500" data-testid="footer-terms">Terms</Link>
                <span className="hidden md:inline">Cottage food disclaimer on FAQ page.</span>
              </div>
            </div>
          </div>
        </footer>
      )}

      {/* Sticky mobile Order Now button */}
      {!isAdminRoute && (
        <Link
          to="/custom-orders"
          data-testid="sticky-order-btn"
          className="lg:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-40 btn-primary shadow-sakuraStrong"
        >
          <Sparkles className="h-4 w-4" /> Order Now
        </Link>
      )}
    </div>
  );
}
